/**
 * Saved products ("saved" wishlist), kept in the visitor's browser.
 * - <save-button data-handle>: toggles a product in the list (product page, saved cards).
 * - <saved-products>: the saved page. Renders one card per product through the Section Rendering API
 *   (saved-product-card), shows the count, shares the list as a link (?items=a,b) and handles each
 *   card's size buttons and add-to-bag.
 */
import { CartLinesUpdateEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

const STORAGE_KEY = 'inebas:saved-products';
const CHANGE_EVENT = 'saved-products:change';

/** @returns {string[]} */
function getSaved() {
  try {
    const value = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]');
    return Array.isArray(value) ? value.filter((handle) => typeof handle === 'string') : [];
  } catch {
    return [];
  }
}

/** @param {string[]} handles */
function setSaved(handles) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
  } catch {
    // Storage blocked (private mode, settings): the list just won't persist
  }
  document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: handles }));
}

class SaveButton extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.button?.addEventListener('click', this.#toggle);
    document.addEventListener(CHANGE_EVENT, this.#render);
    this.#render();
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.#toggle);
    document.removeEventListener(CHANGE_EVENT, this.#render);
  }

  get #handle() {
    return this.dataset.handle ?? '';
  }

  #toggle = () => {
    const saved = getSaved();
    const handle = this.#handle;
    setSaved(saved.includes(handle) ? saved.filter((h) => h !== handle) : [handle, ...saved]);
  };

  #render = () => {
    if (!this.button) return;
    const isSaved = getSaved().includes(this.#handle);
    this.button.setAttribute('aria-pressed', String(isSaved));
    const label = (isSaved ? this.dataset.labelSaved : this.dataset.labelSave) ?? '';
    // Icon style keeps its heart icon; the label moves to aria-label
    if (this.dataset.style === 'icon') this.button.setAttribute('aria-label', label);
    else this.button.textContent = label;
  };
}

class SavedProducts extends HTMLElement {
  /** Handles shown from a shared link (?items=...), or null for the visitor's own list */
  #shared = /** @type {string[] | null} */ (null);

  connectedCallback() {
    const items = new URLSearchParams(window.location.search).get('items');
    if (items) {
      this.#shared = items.split(',').map((h) => h.trim()).filter(Boolean);
    }

    this.addEventListener('click', this.#onClick);
    this.addEventListener('submit', this.#onSubmit);
    document.addEventListener(CHANGE_EVENT, this.#onChange);
    this.#render();
  }

  disconnectedCallback() {
    document.removeEventListener(CHANGE_EVENT, this.#onChange);
  }

  get #handles() {
    return this.#shared ?? getSaved();
  }

  /** Unsaving a card on your own list removes it right away */
  #onChange = () => {
    if (this.#shared) return;
    const saved = getSaved();
    for (const card of this.querySelectorAll('[data-saved-card]')) {
      if (card instanceof HTMLElement && !saved.includes(card.dataset.handle ?? '')) card.remove();
    }
    this.#updateCount();
  };

  async #render() {
    const list = this.querySelector('[data-saved-list]');
    const empty = this.querySelector('[data-saved-empty]');
    if (!(list instanceof HTMLElement) || !(empty instanceof HTMLElement)) return;

    const handles = this.#handles;
    const root = window.Shopify?.routes?.root ?? '/';

    const cards = await Promise.all(
      handles.map(async (handle) => {
        try {
          const url = `${root}products/${encodeURIComponent(handle)}?section_id=saved-product-card`;
          const response = await fetch(url);
          if (!response.ok) return { handle, html: null, gone: response.status === 404 };
          return { handle, html: await response.text(), gone: false };
        } catch {
          return { handle, html: null, gone: false };
        }
      })
    );

    // Forget products that no longer exist
    const gone = cards.filter((card) => card.gone).map((card) => card.handle);
    if (gone.length && !this.#shared) setSaved(getSaved().filter((handle) => !gone.includes(handle)));

    list.innerHTML = cards.map((card) => card.html ?? '').join('');

    const shared = this.querySelector('[data-saved-shared]');
    if (shared instanceof HTMLElement) shared.hidden = !this.#shared;

    this.#updateCount();
  }

  #updateCount() {
    const list = this.querySelector('[data-saved-list]');
    const empty = this.querySelector('[data-saved-empty]');
    const count = this.querySelector('[data-saved-count]');
    const share = this.querySelector('[data-saved-share]');
    const total = this.querySelectorAll('[data-saved-card]').length;

    if (list instanceof HTMLElement) list.hidden = total === 0;
    if (empty instanceof HTMLElement) empty.hidden = total > 0;
    if (share instanceof HTMLElement) share.hidden = total === 0 || Boolean(this.#shared);
    if (count instanceof HTMLElement) {
      count.hidden = total === 0;
      const template = (total === 1 ? this.dataset.labelItemsOne : this.dataset.labelItemsOther) ?? '[count]';
      count.textContent = template.replace('[count]', String(total));
    }
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target.closest('[data-saved-share]')) {
      this.#share();
      return;
    }

    if (target.closest('[data-saved-keep]') && this.#shared) {
      const saved = getSaved();
      setSaved([...saved, ...this.#shared.filter((h) => !saved.includes(h))]);
      // Show the visitor's own (now merged) list
      this.#shared = null;
      history.replaceState(null, '', window.location.pathname);
      this.#render();
      return;
    }

    const size = target.closest('[data-variant-id]');
    if (size instanceof HTMLButtonElement) {
      this.#selectSize(size);
    }
  };

  /** @param {HTMLButtonElement} size */
  #selectSize(size) {
    const card = size.closest('[data-saved-card]');
    const form = size.closest('form');
    if (!(card instanceof HTMLElement) || !(form instanceof HTMLFormElement)) return;

    for (const button of form.querySelectorAll('[data-variant-id]')) {
      button.setAttribute('aria-checked', String(button === size));
    }
    const input = form.querySelector('input[name="id"]');
    if (input instanceof HTMLInputElement) input.value = size.dataset.variantId ?? '';
    form.setAttribute('data-ready', '');

    const add = form.querySelector('.saved-card__add');
    if (add) {
      add.textContent = size.hasAttribute('data-preorder') ? card.dataset.labelPreorder ?? '' : card.dataset.labelAdd ?? '';
    }
  }

  /** @param {SubmitEvent} event */
  #onSubmit = async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-saved-form]')) return;
    event.preventDefault();

    const card = form.closest('[data-saved-card]');
    const input = form.querySelector('input[name="id"]');
    const add = form.querySelector('.saved-card__add');
    if (!(card instanceof HTMLElement) || !(input instanceof HTMLInputElement) || !(add instanceof HTMLButtonElement)) return;

    // No size yet: on mobile open the sizes, everywhere ask for one
    if (!input.value) {
      form.setAttribute('data-open', '');
      add.textContent = card.dataset.labelSelect ?? '';
      form.querySelector('[data-variant-id]:not([disabled])')?.focus();
      return;
    }

    await this.#addToCart(card, input.value, add);
  };

  /**
   * Adds one of the variant to the cart the way the product form does, so the bag count,
   * the cart drawer and its auto-open all update.
   * @param {HTMLElement} card
   * @param {string} variantId
   * @param {HTMLButtonElement} button
   */
  async #addToCart(card, variantId, button) {
    const label = button.textContent;
    button.disabled = true;

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
      .filter(Boolean);
    if (sectionIds.length) formData.append('sections', sectionIds.join(','));

    const deferred = CartLinesUpdateEvent.createPromise();
    card.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      // Horizon declares `const Theme` in a classic script: a global binding, not a window property
      const routes = typeof Theme !== 'undefined' ? Theme.routes : {};
      const root = window.Shopify?.routes?.root ?? '/';
      const config = fetchConfig('javascript', { body: formData });
      const response = await fetch(routes.cart_add_url ?? `${root}cart/add.js`, {
        ...config,
        headers: { ...config.headers, Accept: 'text/html' },
      }).then((r) => r.json());

      const cart = await fetch(`${routes.cart_url ?? `${root}cart`}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }).then((r) => r.json());

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'saved-products',
          sourceId: card.dataset.handle ?? '',
          itemCount: 1,
          productId: card.dataset.productId,
          sections: response.sections,
          didError: Boolean(response.status),
        },
      });

      if (response.status) {
        this.#announce(response.message ?? response.description ?? '');
        button.textContent = label;
      } else {
        button.textContent = card.dataset.labelAdded ?? label;
        this.#announce(card.dataset.labelAdded ?? '');
        setTimeout(() => (button.textContent = label), 2000);
      }
    } catch (error) {
      deferred.reject(error);
      button.textContent = label;
    } finally {
      button.disabled = false;
    }
  }

  async #share() {
    const root = window.Shopify?.routes?.root ?? '/';
    const url = new URL(`${root}pages/saved`, window.location.origin);
    url.searchParams.set('items', getSaved().join(','));
    const title = this.dataset.labelShareTitle ?? document.title;

    try {
      if (navigator.share) {
        await navigator.share({ title, url: url.toString() });
        return;
      }
      await navigator.clipboard.writeText(url.toString());
      this.#announce(this.dataset.labelCopied ?? '');
    } catch {
      // Share sheet closed or clipboard blocked: nothing to do
    }
  }

  /** @param {string} message */
  #announce(message) {
    const status = this.querySelector('[data-saved-status]');
    if (!status) return;
    status.textContent = message;
    clearTimeout(this.statusTimer);
    this.statusTimer = setTimeout(() => (status.textContent = ''), 4000);
  }
}

if (!customElements.get('save-button')) customElements.define('save-button', SaveButton);
if (!customElements.get('saved-products')) customElements.define('saved-products', SavedProducts);
